import {StageLayer} from "@nolimitcity/slot-launcher/bin/StageLayer";
import {GuiToggleButton} from "@nolimitcity/slot-launcher/bin/gui/buttons/GuiToggleButton";
import {TheBorder} from "../TheBorder";
import {BonusFeatureData} from "@nolimitcity/slot-launcher/bin/plugins/apiplugin/BonusFeatures";
import {BetBoostData} from "@nolimitcity/slot-launcher/bin/plugins/apiplugin/bethandler/BetHandler";
import {PointerStateColorSet} from "@nolimitcity/slot-launcher/bin/gui/buttons/states/sets/PointerStateColorSet";
import {IconToggleButton} from "@nolimitcity/slot-launcher/bin/gui/buttons/IconToggleButton";
import {Icon} from "@nolimitcity/slot-launcher/bin/gui/displayobjects/Icon";
import {PointerStateIconSet} from "@nolimitcity/slot-launcher/bin/gui/buttons/states/sets/PointerStateIconSet";
import {APIBetEventData} from "@nolimitcity/slot-launcher/bin/interfaces/APIBetEventData";
import {APIBetType} from "@nolimitcity/slot-launcher/bin/interfaces/APIBetType";
import {APIEvent} from "@nolimitcity/slot-launcher/bin/interfaces/APIEventSystem";
import {gsap} from "gsap";
import {TheBorderSound} from "../sound/TheBorderSound";
import {TheBorderGameAssets} from "../TheBorderGameAssets";
import {SpineTween} from "@nolimitcity/slot-game/bin/animation/SpineTween";
import {TheBorderGameMode} from "../server/TheBorderGameMode";
import {TimelineSprite} from "@nolimitcity/slot-launcher/bin/animation/TimelineSprite";
import {MathHelper} from "@nolimitcity/slot-game/bin/utils/MathHelper";

export class TheBorderXRipButtonView extends PIXI.Container implements StageLayer {
    private _isFirstTime: boolean = true;
    private _boostButton: GuiToggleButton;
    private _boostSpine: PIXI.spine.Spine;
    private _boostSpineTl: gsap.core.Timeline;
    private _betBoostAllowed: boolean = false;
    private _betBoostPaused: boolean = false;
    private _highlightSparks: TimelineSprite;
    private _randomTl: gsap.core.Timeline;
    private _isFreeBet: boolean = false;
    private _boostButtonOff: PIXI.Sprite;

    private _enableTl: gsap.core.Timeline;

    constructor() {
        super();
        this.name = "TheBorderXRipButton";
        this._isFirstTime = true;
        this.init();

        if (TheBorder.apiPlugin.isReplay) {
            if (TheBorder.model.nextPlayerInteraction?.featureName == "BOOSTED_BET") {
                this._betBoostAllowed = false;
                this.stopHighlightSparks();
                const tl:gsap.core.Timeline = gsap.timeline();
                tl.add(() => {
                    this.resetSpine();
                    TheBorder.xRipReelHighlighter.highlightReel();
                });
                tl.add(new SpineTween(this._boostSpine, 0, "on"));
                tl.add(() => {
                    this.resetSpine();
                });
                tl.add(new SpineTween(this._boostSpine, 0, "idle_on", -1));
            }
        } else if (TheBorder.apiPlugin.freeBets.hasFreeBets()) {
            this.freeBetsStart();
            TheBorder.apiPlugin.freeBets.events.once(TheBorder.apiPlugin.freeBets.FREE_BETS_END, () => {
                this.freeBetsEnd();
            });
        } else if (TheBorder.model.isRestore && TheBorder.model.enhancedBet) {
            this.turnOnBoost(false);
            this.updateBoostBtnState();
        }

        TheBorder.apiPlugin.freeBets.events.on(TheBorder.apiPlugin.freeBets.FREE_BETS_START, () => {
            this.freeBetsStart();

            TheBorder.apiPlugin.freeBets.events.once(TheBorder.apiPlugin.freeBets.FREE_BETS_END, () => {
                this.freeBetsEnd();
            });
        });
        TheBorder.apiPlugin.events.on(APIEvent.BET, (data: APIBetEventData) => this.onBet(data));

        TheBorder.apiPlugin.events.on("extFreeRoundsEnded", () => {
            this.freeBetsEnd();
        });
    }

    private freeBetsStart(): void {
        this.turnOffBoost(true);
        this._isFreeBet = true;
        this.stopHighlightSparks();
        this._boostSpine.visible = false;
        this._boostButtonOff.visible = true;
    }

    private freeBetsEnd(): void {
        this.turnOffBoost(false);
        this._isFreeBet = false;
        this.playHighLightSparks();
        this._boostSpine.visible = true;
        this._boostButtonOff.visible = false;
    }

    private init(): void {
        this._betBoostAllowed = TheBorder.apiPlugin.gameClientConfiguration.boostedBetAllowed;
        if (this._betBoostAllowed) {
            this.createBoostButton();
        }
        if (TheBorder.model.isRestore && (TheBorder.model.data.mode == TheBorderGameMode.FREESPIN || TheBorder.model.data.mode == TheBorderGameMode.FREESPIN_AVALANCHE
            || TheBorder.model.data.mode == TheBorderGameMode.SUPER_FREESPIN || TheBorder.model.data.mode == TheBorderGameMode.SUPER_FREESPIN_AVALANCHE)) {
            this.hide();
        }
    }

    public enableButton(value: boolean, delay: number): void {
        if (this._boostButton) {
            if (!this._betBoostAllowed || this._betBoostPaused) {
                return;
            }

            // #439
            if (this._enableTl) {
                this._enableTl.pause();
                this._enableTl.kill();
            }

            const tl: gsap.core.Timeline = gsap.timeline();
            tl.add(() => {
                this._boostButton.enable(value);
            }, delay);

            this._enableTl = tl;
        }
    }

    public handleXRipWins(): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();

        tl.add(() => {
            this.resetSpine();
        });
        tl.add(() => {
            TheBorder.soundPlayer.playSound(TheBorderSound.X_RIP_FLASH);
            new SpineTween(this._boostSpine, 0, "blast");
        });
        tl.add(() => {
            TheBorder.keypadPlugin.setWin(0, undefined, undefined, true);
        }, "+=0.2");
        tl.add(() => {
            TheBorder.soundPlayer.playSound(TheBorderSound.X_RIP_POOF);
            TheBorder.xRipBlast.playBlast();
        });
        tl.add(() => {
            this.resetSpine();
            new SpineTween(this._boostSpine, 0, "idle_on", -1);
        });

        return tl;
    }

    public hide(): void {
        this.visible = false;
    }

    public show(): void {
        this.visible = true;
    }

    public playHighLightSparks(): void {
        if (!this._betBoostAllowed || !this.visible || this._isFreeBet) {
            return;
        }

        if (this._randomTl) {
            this._randomTl.pause();
            this._randomTl.kill();
        }

        this._randomTl = gsap.timeline({
            delay: this._isFirstTime ? 1 : MathHelper.randomIntInRange(7, 15)
        });

        this._randomTl.add(() => {
            if (this._betBoostAllowed && this.visible && !TheBorder.IS_BOOSTED_BET_ON) {
                this._highlightSparks.getAnimationAutoShowHide(true, true);

                // #404
                TheBorder.soundPlayer.playSound(TheBorderSound.BOOSTER_IDLE_SPARKS.next());
            }

            this._isFirstTime = false;
            this.playHighLightSparks();
        });
    }

    public stopHighlightSparks(): void {
        if (this._highlightSparks) {
            this._highlightSparks.hide();
        }

        if (this._randomTl) {
            this._randomTl.pause();
            this._randomTl.kill();
        }
    }

    private createBoostButton(): void {
        const colorSet: PointerStateColorSet = new PointerStateColorSet(
                0xFFFFFFFF,
                0xFFFFFFFF,
                0xFFFFFFFF,
                0xFFFFFFFF
            ),
            textureName: string = "Boost/boostButton",
            onTextureName: string = textureName + "On",
            offTextureName: string = textureName + "Off";

        this._boostButton = new IconToggleButton("boost",
            new PointerStateIconSet(
                new Icon(TheBorder.assetLoader.getTextures(onTextureName)[0]),
                new Icon(TheBorder.assetLoader.getTextures(onTextureName)[0]),
                new Icon(TheBorder.assetLoader.getTextures(onTextureName)[0]),
                new Icon(TheBorder.assetLoader.getTextures(onTextureName)[0])
            ),
            colorSet,
            new PointerStateIconSet(
                new Icon(TheBorder.assetLoader.getTextures(offTextureName)[0]),
                new Icon(TheBorder.assetLoader.getTextures(offTextureName)[0]),
                new Icon(TheBorder.assetLoader.getTextures(offTextureName)[0]),
                new Icon(TheBorder.assetLoader.getTextures(offTextureName)[0])
            ),
            colorSet);

        this._boostButton.addClickCallback(() => this.boostBtnClick());
        this._boostButton.position.set(0, 3);
        this._boostButton.toggled = false;
        this._boostButton.enable(false);

        this._boostSpine = new PIXI.spine.Spine(TheBorder.assetLoader.getSpineAsset(TheBorderGameAssets.X_RIP_BUTTON));
        this._boostSpine.position.set(this._boostSpine.width / 2, this._boostSpine.height / 2);
        new SpineTween(this._boostSpine, 0, "idle_off", -1);

        this._highlightSparks = new TimelineSprite(TheBorder.assetLoader.getTextures("boosted_sparks/boosted_sparks_"));
        this._highlightSparks.hide();
        this._highlightSparks.position.set(-8, 5);
        this._highlightSparks.blendMode = PIXI.BLEND_MODES.ADD;
        this._highlightSparks.playLoop();

        this._boostButtonOff = new PIXI.Sprite(TheBorder.assetLoader.getTextures(TheBorderGameAssets.BOOST_BUTTON_OFF)[0]);
        this._boostButtonOff.visible = false;

        this.addChild(this._boostButton, this._boostSpine, this._boostButtonOff, this._highlightSparks);
    }

    private onBet(data: APIBetEventData): void {
        if (data.type == APIBetType.FEATURE_BET && data.playerInteraction.featureName !== "BOOSTED_BET") {
            this.turnOffBoost(false, true);
        }
        this.enableButton(false, 0);
    }

    public turnOnBoost(pauseBoost: boolean) : void {
        if (!this._betBoostAllowed) {
            return;
        }

        TheBorder.apiPlugin.betFeatureController.setActiveBetFeature("BOOSTED_BET");
        this._betBoostPaused = pauseBoost;
    }

    public turnOffBoost(pauseBoost: boolean, isFeatureBet:boolean = false) : void {
        if (!this._betBoostAllowed) {
            return;
        }
        if (isFeatureBet == false){
            TheBorder.apiPlugin.betHandler.setBoost(undefined);
        }
        this._betBoostPaused = pauseBoost;
    }

    public updateBoostBtnState() : void{
        if (!this._betBoostAllowed || this._betBoostPaused) {
            return;
        }
        const boostIsSet : boolean = (TheBorder.apiPlugin.betFeatureController.getActiveBetFeature()?.name === "BOOSTED_BET");

        if (boostIsSet == TheBorder.IS_BOOSTED_BET_ON){
            return;
        }

        TheBorder.IS_BOOSTED_BET_ON = boostIsSet;
        this._boostButton.toggled = TheBorder.IS_BOOSTED_BET_ON;

        if (this._boostSpineTl) {
            this._boostSpineTl.pause();
            this._boostSpineTl.kill();
        }

        const tl: gsap.core.Timeline = gsap.timeline();
        if (TheBorder.IS_BOOSTED_BET_ON) {
            TheBorder.soundPlayer.playSound(TheBorderSound.BOOSTER_ON);
            TheBorder.soundPlayer.stopSound(TheBorderSound.BOOSTER_OFF);
            this.stopHighlightSparks();
            tl.add(() => {
                this.resetSpine();
                TheBorder.xRipReelHighlighter.highlightReel();
            });
            tl.add(new SpineTween(this._boostSpine, 0, "on"));
            tl.add(() => {
                this.resetSpine();
            });
            tl.add(new SpineTween(this._boostSpine, 0, "idle_on", -1));
        } else {
            TheBorder.soundPlayer.playSound(TheBorderSound.BOOSTER_OFF);
            TheBorder.soundPlayer.stopSound(TheBorderSound.BOOSTER_ON);
            this.playHighLightSparks();
            tl.add(() => {
                this.resetSpine();

                TheBorder.xRipReelHighlighter.stopHighlight();
            });
            tl.add(new SpineTween(this._boostSpine, 0, "off"));
            tl.add(() => {
                this.resetSpine();
            });
            tl.add(new SpineTween(this._boostSpine, 0, "idle_off", -1));
        }

        this._boostSpineTl = tl;
    }

    public boostBtnClick() : void{
        if (!this._betBoostAllowed || this._betBoostPaused) {
            return;
        }
        if (TheBorder.apiPlugin.betFeatureController.getActiveBetFeature()?.name !== "BOOSTED_BET") {
            this.turnOnBoost(this._betBoostPaused);
        } else {
            this.turnOffBoost(this._betBoostPaused);
        }
    }

    private boostToggle(isFromClick: boolean, calledFromBonusBuy: boolean): void {
        if (!this._betBoostAllowed || this._betBoostPaused) {
            return;
        }

        if (this._boostSpineTl) {
            this._boostSpineTl.pause();
            this._boostSpineTl.kill();
        }
        const tl: gsap.core.Timeline = gsap.timeline();
        if (!this._boostButton.toggled) {
            const featureData: BonusFeatureData | undefined = TheBorder.apiPlugin.bonusFeatures.getFeatureDataByName("BOOSTED_BET");
            if (featureData) {
                let data: BetBoostData = {
                    featureName: featureData.name,
                    price: featureData.price
                }
                TheBorder.apiPlugin.betHandler.setBoost(data);
                if (isFromClick) {
                    TheBorder.soundPlayer.playSound(TheBorderSound.BOOSTER_ON);
                }
                TheBorder.soundPlayer.stopSound(TheBorderSound.BOOSTER_OFF);
                this.stopHighlightSparks();

                tl.add(() => {
                    this.resetSpine();

                    TheBorder.xRipReelHighlighter.highlightReel();
                });
                tl.add(new SpineTween(this._boostSpine, 0, "on"));
                tl.add(() => {
                    this.resetSpine();
                });
                tl.add(new SpineTween(this._boostSpine, 0, "idle_on", -1));
            }
        } else {
            if (isFromClick || calledFromBonusBuy) {
                TheBorder.soundPlayer.stopSound(TheBorderSound.BOOSTER_ON);
                TheBorder.soundPlayer.playSound(TheBorderSound.BOOSTER_OFF);
            }
            this.playHighLightSparks();

            tl.add(() => {
                this.resetSpine();

                TheBorder.xRipReelHighlighter.stopHighlight();
            });
            tl.add(new SpineTween(this._boostSpine, 0, "off"));
            tl.add(() => {
                this.resetSpine();
            });
            tl.add(new SpineTween(this._boostSpine, 0, "idle_off", -1));
        }

        this._boostButton.toggled = !this._boostButton.toggled;
        TheBorder.IS_BOOSTED_BET_ON = !TheBorder.IS_BOOSTED_BET_ON;
        this._boostSpineTl = tl;
    }

    private resetSpine(): void {
        if (!this._boostSpine.visible) {
            this._boostSpine.visible = true;
            this._boostSpine.updateTransform();
        }
        this._boostSpine.state.clearTracks();
        this._boostSpine.skeleton.setSlotsToSetupPose();
        this._boostSpine.skeleton.setToSetupPose();
        this._boostSpine.state.setEmptyAnimation(0, 0);
    }
}